/**
 * 跨天未签退收口纯函数（规则1 守卫 + 规则2 派生 + 补签退时间计算）。
 * Spec: docs/superpowers/specs/2026-06-13-web-cross-day-clockout-design.md §4
 */
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import type { OpenSessionInfo } from "./derive";

dayjs.extend(utc);
dayjs.extend(timezone);

/** 后端要求相邻 action_time 严格递增至少 1s（_validate_action_time_monotonic）。 */
const MONOTONE_GAP_MS = 1000;

/** 规则1：today-entry 的 attendanceDate 早于今天业务日 ⇒ 陈旧，按 idle 渲染。 */
export function isStaleEntry(
  attendanceDate: string | null | undefined,
  todayBizDate: string,
): boolean {
  return attendanceDate != null && attendanceDate < todayBizDate;
}

/** 对账查询投影出的未闭合 prior-day 考勤记录。 */
export interface CrossDayRecord {
  attendanceId: string;
  attendanceDate: string; // YYYY-MM-DD
  status: "working" | "in_break";
  lastBreakStartAt: string | null; // ISO
  lastClockInAt: string | null; // ISO
  /** max(action_time) over all sessions（fetch 时由 break_start/clock_in/break_end 取最大）。 */
  latestSessionActionTime: string | null;
  /** 行上的 first_clock_in_at 列。0-session 孤儿行(旧测试数据)session 聚合全 null
   * 时唯一可用的真实时间，作为默认/夹断兜底，避免 epoch(1969)。 */
  firstClockInAt: string | null; // ISO
  /** 该考勤日内最后一条**已结束** task work session 的 ended_at（无则 null）。
   * attendance session 聚合看不到 task 工时——用户 break_end 后继续干活到半夜
   * 忘打卡时，只看 attendance 会把默认签退时刻回填到 break_end，时间线出现
   * "下班后还在干活"的假象。补签退默认时刻要把它一起纳入 max。 */
  lastTaskSessionEndedAt: string | null; // ISO
}

export type CrossDayDetected = "break" | "activity" | "forgot";

export interface CrossDayState {
  attendanceId: string;
  attendanceDate: string;
  status: "working" | "in_break";
  detected: CrossDayDetected;
  /** 需先 finish 的跨天 special task id（无则 null；regular 不收尾）。 */
  openSpecialTaskId: string | null;
}

/** 规则2：由对账记录 + 当前开放 task session 派生跨天展示态。 */
export function deriveCrossDayState(
  record: CrossDayRecord | null,
  openSessions: OpenSessionInfo[],
): CrossDayState | null {
  if (!record) return null;
  const openSpecial =
    openSessions.find((s) => s.taskTypeCode === "special") ?? null;
  const detected: CrossDayDetected =
    record.status === "in_break" ? "break" : openSpecial ? "activity" : "forgot";
  return {
    attendanceId: record.attendanceId,
    attendanceDate: record.attendanceDate,
    status: record.status,
    detected,
    openSpecialTaskId: openSpecial?.taskId ?? null,
  };
}

/** max(非空 ISO) → ISO|null（NaN 的脏值直接跳过，不参与比较）。 */
function maxIso(...xs: Array<string | null | undefined>): string | null {
  let best: string | null = null;
  let bestMs = Number.NEGATIVE_INFINITY;
  for (const x of xs) {
    if (!x) continue;
    const ms = dayjs(x).valueOf();
    if (Number.isNaN(ms)) continue;
    if (ms > bestMs) {
      best = x;
      bestMs = ms;
    }
  }
  return best;
}

/** 昨日业务日末刻（用户时区 endOf('day')）的毫秒时间戳。 */
function bizDayEndMs(attendanceDate: string, userTz: string): number {
  return dayjs.tz(attendanceDate, userTz).endOf("day").valueOf();
}

/**
 * 自动补签退默认时刻（面板可覆盖）：
 *   max(
 *     最后一次 attendance session → clock_in 聚合 → 行上 first_clock_in_at
 *       （0-session 孤儿行兜底，避免 epoch）,
 *     最后一条已结束 task work session 的 ended_at
 *   )
 *
 * 加 task session 那一路是因为 attendance 聚合看不到工时：break_end 之后继续
 * 做 special activity 到半夜忘打卡时，只看 attendance 会把默认时刻回填到
 * break_end，时间线上就出现"下班后 4 小时工作"的假象。
 *
 * 第三路 `openSpecialNowMs`：该考勤日还有 **open** 的 special session 时传入
 * serverNow —— resolve 会在 now 强制 finish 它，工作实际持续到 now。不传这一路
 * 的话 lastTaskSessionEndedAt（只看已结束 session）查不到整段班，默认时刻退回
 * clock_in，一整段班被补签成 1 秒（Candice 2026-07-27 prod 案例）。
 *
 * 上界（业务日末刻）由 computeResolveTimes 统一夹断，这里不重复夹。
 */
export function computeDefaultClockOut(
  record: CrossDayRecord,
  openSpecialNowMs?: number | null,
): string {
  const attendanceBest =
    record.latestSessionActionTime ?? record.lastClockInAt ?? record.firstClockInAt;
  const openSpecialIso =
    openSpecialNowMs != null && Number.isFinite(openSpecialNowMs)
      ? new Date(openSpecialNowMs).toISOString()
      : null;
  const best = maxIso(attendanceBest, record.lastTaskSessionEndedAt, openSpecialIso);
  if (!best) {
    // 不可达不变量：working/in_break 行必有 first_clock_in_at。若到此说明后端
    // 连行级 first_clock_in_at 都缺——大声 dump 便于排查，不静默写错 epoch。
    console.error(
      "[crossDay] computeDefaultClockOut: record has no usable timestamp, shape:",
      record,
    );
    return new Date(0).toISOString();
  }
  return best;
}

export interface ResolveTimes {
  /** in_break 才有：先结束 break 的回填时刻。 */
  endBreakIso: string | null;
  /** clock-out 回填时刻。 */
  clockOutIso: string;
}

/**
 * 把用户选定的 targetIso 夹断为后端可接受的 endBreak/clockOut 时刻：
 * - 上界 ≤ 昨日业务日末刻（避免后端 _resolve_attendance_date 落到次日那行）。
 * - 单调：每个 action_time 严格晚于该行已有最后一条 session（+MONOTONE_GAP_MS）。
 * - in_break：endBreak 在 break_start 之后、为 clockOut 预留 1s。
 */
export function computeResolveTimes(
  status: "working" | "in_break",
  targetIso: string,
  record: CrossDayRecord,
  userTz: string,
): ResolveTimes {
  const dayEndMs = bizDayEndMs(record.attendanceDate, userTz);
  // 0-session 孤儿行无 session 时间,退化到行级 first_clock_in_at,使补签退仍落在
  // 该考勤日(而非 epoch)。
  const latestRef = record.latestSessionActionTime ?? record.firstClockInAt;
  const latestMs = latestRef ? dayjs(latestRef).valueOf() : 0;
  const targetMs = Math.min(dayjs(targetIso).valueOf(), dayEndMs);

  if (status === "in_break") {
    const breakStartMs = record.lastBreakStartAt
      ? dayjs(record.lastBreakStartAt).valueOf()
      : latestMs;
    const endBreakMs = Math.min(
      Math.max(targetMs, breakStartMs + MONOTONE_GAP_MS),
      dayEndMs - MONOTONE_GAP_MS,
    );
    const clockOutMs = Math.min(endBreakMs + MONOTONE_GAP_MS, dayEndMs);
    return {
      endBreakIso: new Date(endBreakMs).toISOString(),
      clockOutIso: new Date(clockOutMs).toISOString(),
    };
  }
  const clockOutMs = Math.min(Math.max(targetMs, latestMs + MONOTONE_GAP_MS), dayEndMs);
  return { endBreakIso: null, clockOutIso: new Date(clockOutMs).toISOString() };
}
